import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMediaQuery } from '@react-hookz/web';
import { useHistory } from 'react-router';
import { useTheme } from 'styled-components';

import { HeartAnimation } from 'src/app/components/Elements/HeartAnimation';
import Spinner from 'src/app/components/Elements/Spinner';
import ProgressForm from 'src/app/components/Form/ProgressForm';
import { ConfirmationForm } from 'src/app/components/Form/ProgressForm/ConfirmationForm';
import { DefaultTransferForm } from 'src/app/components/Pages/LoggedInTransfer/Experiment/constants';
import { STEP_NAME } from 'src/app/components/Pages/LoggedInTransfer/Experiment/types';
import { useAccount } from 'src/app/hooks/data/account';
import { usePatientList } from 'src/app/hooks/data/patient';
import { usePendingPrescriptionTransfers } from 'src/app/hooks/data/prescriptionTransfers';
import { useUser } from 'src/app/hooks/data/user';
import {
  useLoggedInTransferFormData,
  useLoggedInTransferFormProgress,
} from 'src/app/hooks/feature_data/useLoggedInTransferFormData';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { medications } from 'src/app/router/paths';
import type { HomeItem, PatientDetails } from 'src/app/types';
import { INTERSTITIAL_TIMING } from 'src/consts';

import { LoggedInTransferAdditional } from './Additional';
import { LoggedInTransferConfirmation } from './Confirmation';
import { LoggedInTransferDoctor } from './Doctor';
import { FamilyFormRenderer } from './Family';
import { LoggedInTransferIntro } from './Intro';
import { LoggedInTransferMedication } from './Medication';
import { LoggedInTransferPharmacySearch } from './Pharmacy';

export type RenderedStepComponentProps = {
  handleNextClick: () => void;
  patients: PatientDetails[];
  setShowCopyrightFooter: Dispatch<SetStateAction<boolean>>;
  zipCode: string;
};

const RenderedStepComponent = ({
  currentStep,
  setSteps,
  pendingTransfers,
  ...props
}: {
  currentStep: STEP_NAME;
  pendingTransfers: HomeItem[];
  setSteps: Dispatch<SetStateAction<STEP_NAME[]>>;
} & RenderedStepComponentProps) => {
  useSegmentEvent('viewLoggedInTransferForm', {
    shouldTrackOnMount: true,
    payload: {
      pagepath: window.location.pathname,
      form_type: 'Paginated Logged In Transfer Form',
      pending_requests: pendingTransfers.length,
    },
  });

  switch (currentStep) {
    case STEP_NAME.PATIENT:
      return <FamilyFormRenderer {...props} />;
    case STEP_NAME.INTRO:
      return <LoggedInTransferIntro setSteps={setSteps} {...props} />;
    case STEP_NAME.ADDITIONAL_INFO:
      return <LoggedInTransferAdditional {...props} />;
    case STEP_NAME.DOCTOR:
      return <LoggedInTransferDoctor {...props} />;
    case STEP_NAME.PHARMACY:
      return <LoggedInTransferPharmacySearch {...props} />;
    case STEP_NAME.MEDICATIONS:
      return <LoggedInTransferMedication {...props} />;
    default:
      return <Spinner />;
  }
};

export const LoggedInTransferForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.mediumDown);

  const selectLoggedInTransferFormUserEvent = useSegmentEvent('selectLoggedInTransferFormUser', {
    shouldTrackOnMount: false,
  });

  const { push } = useHistory();
  const { loggedInTransferFormData, setLoggedInTransferFormData } = useLoggedInTransferFormData();
  const { loggedInTransferFormProgress, setLoggedInTransferFormProgress } = useLoggedInTransferFormProgress();

  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const { data: patients, isLoading: isPatientListLoading } = usePatientList(
    user ? parseInt(user.accountId) : -1,
    Boolean(user)
  );

  const { data: pendingTransfers, isLoading: isLoadingPendingTransfers } = usePendingPrescriptionTransfers(
    user ? parseInt(user.accountId) : -1,
    Boolean(user)
  );

  const { data: account } = useAccount(user ? parseInt(user.accountId) : -1, {
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const zipCode = account?.zipCode || '';

  const [steps, setSteps] = useState<STEP_NAME[]>(loggedInTransferFormProgress?.steps || DefaultTransferForm);

  const [currentStepIndex, setCurrentStepIndex] = useState(loggedInTransferFormProgress?.currentStepIndex || 0);

  const currentStep = useMemo(() => {
    return steps[currentStepIndex];
  }, [steps, currentStepIndex]);

  useEffect(() => {
    setLoggedInTransferFormProgress({
      steps: steps,
      currentStepIndex: currentStepIndex,
    });
  }, [steps, currentStepIndex]);

  // Determines whether to show the patient select page. If there's only one patient in the account, don't show
  useEffect(() => {
    if (!isPatientListLoading && patients && !loggedInTransferFormData?.patientId) {
      if (patients && patients.length === 1) {
        const patient = patients[0];

        setLoggedInTransferFormData({
          ...loggedInTransferFormData,
          patientId: patient.id,
          patientName: patient.firstName,
          patientIsPrimary: patient.isPrimary,
          birthdate: patient.birthdate,
        });

        selectLoggedInTransferFormUserEvent({
          account_holder: 'Primary',
        });

        setSteps(prevSteps => prevSteps.filter(step => step !== STEP_NAME.PATIENT));
      }
    }
  }, [isPatientListLoading, patients]);

  useEffect(() => {
    // Only add/remove Additional Info step when we have populated the transfer form with initial patient data.
    if (!isPatientListLoading && loggedInTransferFormData && Object.keys(loggedInTransferFormData).length > 0) {
      const persistedBirthdate = loggedInTransferFormData?.birthdate;
      if (!persistedBirthdate || persistedBirthdate.startsWith('1900-01-01')) {
        setSteps(prevSteps => {
          if (!prevSteps.includes(STEP_NAME.ADDITIONAL_INFO)) {
            return [...prevSteps.slice(0, -1), STEP_NAME.ADDITIONAL_INFO, prevSteps[prevSteps.length - 1]];
          }
          return prevSteps;
        });
      } else {
        setSteps(prevSteps => prevSteps.filter(step => step !== STEP_NAME.ADDITIONAL_INFO));
      }
    }
  }, [isPatientListLoading, loggedInTransferFormData?.patientId]);

  const [shouldShowCopyrightFooter, setShowCopyrightFooter] = useState(true);

  const isLoading = isPatientListLoading || !currentStep || !user || isLoadingPendingTransfers;

  // Interstitial heart page
  const [shouldShowInterstitial, setShouldShowInterstitial] = useState(false);
  const showHeart = useCallback((callback?: () => void) => {
    setShouldShowInterstitial(true);
    window.setTimeout(() => {
      callback?.();
      setShouldShowInterstitial(false);
    }, INTERSTITIAL_TIMING);
  }, []);

  if (isLoading) return <Spinner centered />;

  if (shouldShowInterstitial) return <HeartAnimation />;

  const hasOneStep = steps.length === 1;
  const isOnFirstStep = currentStep && steps.indexOf(currentStep) === 0;

  return currentStep === STEP_NAME.CONFIRMATION ? (
    <ConfirmationForm>
      <LoggedInTransferConfirmation showHeart={showHeart} />
    </ConfirmationForm>
  ) : (
    <ProgressForm
      steps={steps}
      currentStepIndex={(currentStep && steps.indexOf(currentStep)) || 0}
      onPrev={() => {
        if (isOnFirstStep) {
          push(medications.toPath());
        } else {
          setCurrentStepIndex(prev => prev - 1);
        }
      }}
      isHorizontalProgressBarHiddenOnDesktop={false}
      isHorizontalProgressBarVisible={isMobile && !hasOneStep}
      isVerticalProgressBarVisible={!hasOneStep}
      isStepCountVisible={!hasOneStep}
      mainRowPaddingBottom="none"
      showCopyrightFooter={shouldShowCopyrightFooter}
    >
      <RenderedStepComponent
        currentStep={currentStep}
        setSteps={setSteps}
        patients={patients ?? []}
        pendingTransfers={pendingTransfers ?? []}
        handleNextClick={() => setCurrentStepIndex(prev => prev + 1)}
        setShowCopyrightFooter={setShowCopyrightFooter}
        zipCode={zipCode}
      />
    </ProgressForm>
  );
};
